import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ChoiceGroup, ConsentChecks, DonePanel, Field, GoldButton, Slider, ToolShell,
  fmt, fmtPct, inputCls, parseNum, selectCls,
} from "@/components/FormUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL = "Rangos de mercado orientativos elaborados a partir de índices públicos y condiciones habituales en el mercado español. Las condiciones reales dependen de cada entidad y del análisis de riesgo de tu empresa. No constituye una oferta de financiación ni intermediación crediticia.";

export default function Circulante() {
  const navigate = useNavigate();
  const [diasDirectos, setDiasDirectos] = useState(false);
  const [step1, setStep1] = useState({
    ventas: "", compras: "", coste_ventas: "", clientes: "", existencias: "", proveedores: "",
    pmc: "", pmi: "", pmp: "", sector: "",
  });
  const [cycle, setCycle] = useState(null);
  const [sliders, setSliders] = useState({ pmc: 0, pmi: 0, pmp: 0 });
  const [finForm, setFinForm] = useState({
    nof: "", antiguedad: "", dfn: "", facturacion: "",
    actuales: [], plazo_dias: "45", dpp_pct: "", dpp_dias: "",
  });
  const [fin, setFin] = useState(null);
  const [lead, setLead] = useState({ nombre: "", empresa: "", email: "", telefono: "", consent_privacidad: false, consent_comercial: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  useEffect(() => {
    document.title = "Diagnóstico de circulante | AJG M&A Consulting";
  }, []);

  const set1 = (k) => (e) => setStep1({ ...step1, [k]: e.target.value });

  const step1Payload = () => ({
    ventas: parseNum(step1.ventas), compras: parseNum(step1.compras),
    coste_ventas: step1.coste_ventas ? parseNum(step1.coste_ventas) : null,
    clientes: step1.clientes ? parseNum(step1.clientes) : null,
    existencias: step1.existencias ? parseNum(step1.existencias) : null,
    proveedores: step1.proveedores ? parseNum(step1.proveedores) : null,
    dias_directos: diasDirectos,
    pmc: diasDirectos ? parseNum(step1.pmc) : null,
    pmi: diasDirectos ? parseNum(step1.pmi) : null,
    pmp: diasDirectos ? parseNum(step1.pmp) : null,
    sector: step1.sector || null,
  });

  const submitCycle = async (e) => {
    e.preventDefault();
    const p = step1Payload();
    if (!(p.ventas > 0) || !(p.compras > 0) || (!diasDirectos && (p.clientes == null || p.existencias == null || p.proveedores == null)) ||
        (diasDirectos && (Number.isNaN(p.pmc) || Number.isNaN(p.pmi) || Number.isNaN(p.pmp)))) {
      toast.error("Completa todos los campos obligatorios del ciclo de caja.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/circulante/analyze`, p);
      setCycle(data);
      setFinForm((f) => ({ ...f, nof: String(Math.max(0, data.nof)) }));
      setSliders({ pmc: 0, pmi: 0, pmp: 0 });
      setFin(null);
      setDone(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo calcular el ciclo.");
    } finally {
      setLoading(false);
    }
  };

  const submitFin = async (e) => {
    e.preventDefault();
    if (!finForm.antiguedad || !finForm.dfn || !finForm.facturacion) {
      toast.error("Completa el perfil de la empresa.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/circulante/financiacion`, {
        nof: parseNum(finForm.nof) || cycle?.nof || 0,
        perfil: { antiguedad: finForm.antiguedad, dfn_ebitda: finForm.dfn, facturacion: finForm.facturacion },
        actuales: finForm.actuales.filter((a) => a.tipo !== ""),
        plazo_dias: parseInt(finForm.plazo_dias, 10) || 45,
        dpp_pct: finForm.dpp_pct ? parseNum(finForm.dpp_pct) : null,
        dpp_dias: finForm.dpp_dias ? parseNum(finForm.dpp_dias) : null,
      });
      setFin(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo calcular la financiación.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/circulante/lead`, {
        ciclo: step1Payload(),
        financiacion: {
          nof: parseNum(finForm.nof) || cycle.nof,
          perfil: { antiguedad: finForm.antiguedad, dfn_ebitda: finForm.dfn, facturacion: finForm.facturacion },
          actuales: finForm.actuales.filter((a) => a.tipo !== ""),
          plazo_dias: parseInt(finForm.plazo_dias, 10) || 45,
          dpp_pct: finForm.dpp_pct ? parseNum(finForm.dpp_pct) : null,
          dpp_dias: finForm.dpp_dias ? parseNum(finForm.dpp_dias) : null,
        },
        nombre: lead.nombre, empresa: lead.empresa, email: lead.email, telefono: lead.telefono,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
      });
      setDone(true);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLeadLoading(false);
    }
  };

  const toggleActual = (key) => {
    const exists = finForm.actuales.find((a) => a.instrumento === key);
    setFinForm({
      ...finForm,
      actuales: exists
        ? finForm.actuales.filter((a) => a.instrumento !== key)
        : [...finForm.actuales, { instrumento: key, tipo: "", comision: "" }],
    });
  };

  const liberada = cycle
    ? sliders.pmc * -1 * cycle.caja_dia_pmc + sliders.pmi * -1 * cycle.caja_dia_pmi + sliders.pmp * cycle.caja_dia_pmp
    : 0;

  const INSTRUMENTOS = fin?.instrumentos?.map((i) => i.key) || [
    "poliza", "prestamo", "descuento", "factoring_recurso", "factoring_sin_recurso", "confirming", "alternativa",
  ];
  const NOMBRES = {
    poliza: "Póliza de crédito", prestamo: "Préstamo de circulante", descuento: "Descuento / anticipo de facturas",
    factoring_recurso: "Factoring con recurso", factoring_sin_recurso: "Factoring sin recurso",
    confirming: "Confirming", alternativa: "Financiación alternativa",
  };

  const goServicios = () => {
    navigate("/");
    setTimeout(() => document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" }), 450);
  };

  return (
    <ToolShell
      eyebrow="Herramientas · Diagnóstico de circulante"
      title="¿Cuánta caja tienes inmovilizada en tu ciclo operativo?"
      intro="Calcula tu ciclo de conversión de caja, simula cuánto podrías liberar ajustando cobros, inventario y pagos, y compara el coste real de cada instrumento de financiación del circulante."
    >
      {/* PASO 1 */}
      <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-start">
        <form onSubmit={submitCycle} className="space-y-6" data-testid="circulante-step1-form">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Paso 1 · Ciclo de caja (último ejercicio)</p>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={diasDirectos} onChange={(e) => setDiasDirectos(e.target.checked)} className="h-4 w-4 accent-gold" data-testid="circulante-toggle-dias" />
              Conozco mis días
            </label>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Ventas anuales (€)">
              <input data-testid="circ-ventas" value={step1.ventas} onChange={set1("ventas")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Compras / aprovisionamientos (€)">
              <input data-testid="circ-compras" value={step1.compras} onChange={set1("compras")} inputMode="numeric" className={inputCls} />
            </Field>
          </div>
          {diasDirectos ? (
            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Días de cobro (PMC)">
                <input data-testid="circ-pmc" value={step1.pmc} onChange={set1("pmc")} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Días de inventario (PMI)">
                <input data-testid="circ-pmi" value={step1.pmi} onChange={set1("pmi")} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Días de pago (PMP)">
                <input data-testid="circ-pmp" value={step1.pmp} onChange={set1("pmp")} inputMode="numeric" className={inputCls} />
              </Field>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Coste de ventas (€)" hint="Opcional; si no se conoce, se usan los aprovisionamientos">
                <input data-testid="circ-coste" value={step1.coste_ventas} onChange={set1("coste_ventas")} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Saldo de clientes a cierre (€)">
                <input data-testid="circ-clientes" value={step1.clientes} onChange={set1("clientes")} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Saldo de existencias a cierre (€)">
                <input data-testid="circ-existencias" value={step1.existencias} onChange={set1("existencias")} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Saldo de proveedores a cierre (€)">
                <input data-testid="circ-proveedores" value={step1.proveedores} onChange={set1("proveedores")} inputMode="numeric" className={inputCls} />
              </Field>
            </div>
          )}
          <Field label="Sector (opcional, para la comparativa sectorial)">
            <select data-testid="circ-sector" value={step1.sector} onChange={set1("sector")} className={selectCls}>
              <option value="" className="bg-ink-2">Sin especificar</option>
              {["Industrial y manufactura", "Distribución y comercio mayorista", "Construcción", "Servicios profesionales", "Tecnología y software", "Hostelería y restauración", "Comercio minorista", "Transporte y logística", "Otros"].map((s) => (
                <option key={s} value={s} className="bg-ink-2 text-white">{s}</option>
              ))}
            </select>
          </Field>
          <GoldButton loading={loading} testid="circ-step1-submit">Calcular mi ciclo de caja</GoldButton>
        </form>

        <div className="lg:sticky lg:top-10">
          {cycle ? (
            <div className="relative border border-gold/20 bg-ink-3/60 p-8" data-testid="circ-result">
              <div className="pointer-events-none absolute -left-2 -top-2 h-5 w-5 border-l border-t border-gold" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Tu ciclo de conversión de caja</p>

              <div className="mt-6">
                <div className="flex h-10 w-full overflow-hidden">
                  <div className="bg-gold/25" style={{ width: `${(cycle.pmi / Math.max(cycle.pmc + cycle.pmi, cycle.pmp)) * 100}%` }} />
                  <div className="bg-gold/60" style={{ width: `${(cycle.pmc / Math.max(cycle.pmc + cycle.pmi, cycle.pmp)) * 100}%` }} />
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-slate-400">
                  <span>Compra · inventario: {cycle.pmi} días</span>
                  <span>Venta → cobro: {cycle.pmc} días</span>
                </div>
                <div className="mt-2 h-2.5 bg-slate-500/40" style={{ width: `${(cycle.pmp / Math.max(cycle.pmc + cycle.pmi, cycle.pmp)) * 100}%` }} />
                <p className="mt-1 text-[11px] text-slate-400">Pagas a proveedores a los {cycle.pmp} días</p>
                <p className="mt-5 font-serif text-2xl text-white">
                  CCC: <span className="italic text-gold" data-testid="circ-ccc">{cycle.ccc} días</span>
                </p>
              </div>

              <p className="mt-8 font-serif text-3xl leading-snug text-white" data-testid="circ-nof">
                Tienes <span className="text-gold">{fmt(cycle.nof)} €</span> inmovilizados en tu ciclo operativo
              </p>
              <p className="mt-2 text-sm text-slate-400">Equivale a {cycle.nof_dias} días de venta.</p>

              <div className="mt-8 space-y-5 border-t border-gold/10 pt-7">
                <Slider label="Días de cobro (PMC)" value={sliders.pmc} min={-30} max={30} onChange={(v) => setSliders({ ...sliders, pmc: v })} testid="circ-slider-pmc" suffix=" días" />
                <Slider label="Días de inventario (PMI)" value={sliders.pmi} min={-30} max={30} onChange={(v) => setSliders({ ...sliders, pmi: v })} testid="circ-slider-pmi" suffix=" días" />
                <Slider label="Días de pago (PMP)" value={sliders.pmp} min={-30} max={30} onChange={(v) => setSliders({ ...sliders, pmp: v })} testid="circ-slider-pmp" suffix=" días" />
                <p className="font-serif text-xl text-white" data-testid="circ-liberada">
                  Caja {liberada >= 0 ? "liberada" : "adicional necesaria"}:{" "}
                  <span className="text-gold">{fmt(Math.abs(liberada))} €</span>
                </p>
              </div>

              {cycle.sector_ref && (
                <div className="mt-6 border-t border-gold/10 pt-5 text-sm text-slate-400" data-testid="circ-sector-ref">
                  Referencia sectorial orientativa: PMC mediano {cycle.sector_ref.pmc} días · PMP mediano {cycle.sector_ref.pmp} días.
                </div>
              )}
            </div>
          ) : (
            <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="circ-empty">
              <p className="text-sm leading-relaxed text-slate-400">
                Introduce las cifras de tu último ejercicio y obtén al instante tu ciclo de conversión de caja y la caja inmovilizada.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* PASO 2 */}
      {cycle && (
        <div className="mt-20 border-t border-gold/10 pt-16">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <form onSubmit={submitFin} className="space-y-6" data-testid="circulante-step2-form">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Paso 2 · Coste de la financiación del circulante</p>
              <Field label="Importe de NOF a financiar (€)">
                <input data-testid="circ-nof-input" value={finForm.nof} onChange={(e) => setFinForm({ ...finForm, nof: e.target.value })} inputMode="numeric" className={inputCls} />
              </Field>
              <Field label="Antigüedad de la empresa">
                <ChoiceGroup testid="circ-perfil-ant" options={[{ v: "<3", l: "<3 años" }, { v: "3-10", l: "3-10 años" }, { v: ">10", l: ">10 años" }]} value={finForm.antiguedad} onChange={(v) => setFinForm({ ...finForm, antiguedad: v })} />
              </Field>
              <Field label="Deuda financiera neta / EBITDA">
                <ChoiceGroup testid="circ-perfil-dfn" options={[{ v: "<1", l: "<1x" }, { v: "1-3", l: "1-3x" }, { v: ">3", l: ">3x" }, { v: "negativo", l: "EBITDA negativo" }]} value={finForm.dfn} onChange={(v) => setFinForm({ ...finForm, dfn: v })} />
              </Field>
              <Field label="Facturación">
                <ChoiceGroup testid="circ-perfil-fact" options={[{ v: "<2", l: "<2 M€" }, { v: "2-10", l: "2-10 M€" }, { v: ">10", l: ">10 M€" }]} value={finForm.facturacion} onChange={(v) => setFinForm({ ...finForm, facturacion: v })} />
              </Field>
              <Field label="Instrumentos que usas actualmente (y tu tipo real, si lo conoces)">
                <div className="space-y-3">
                  {Object.entries(NOMBRES).map(([k, nombre]) => {
                    const sel = finForm.actuales.find((a) => a.instrumento === k);
                    return (
                      <div key={k}>
                        <button
                          type="button"
                          onClick={() => toggleActual(k)}
                          data-testid={`circ-inst-${k}`}
                          className={`w-full border px-4 py-2.5 text-left text-sm transition-all ${sel ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}
                        >
                          {nombre} {sel ? "✓" : ""}
                        </button>
                        {sel && (
                          <div className="mt-2 flex gap-3">
                            <input
                              placeholder="Tu tipo % (ej.: 4,5)"
                              value={sel.tipo}
                              onChange={(e) => setFinForm({ ...finForm, actuales: finForm.actuales.map((a) => a.instrumento === k ? { ...a, tipo: e.target.value } : a) })}
                              className={inputCls}
                              data-testid={`circ-inst-tipo-${k}`}
                            />
                            <input
                              placeholder="Comisiones % (opcional)"
                              value={sel.comision}
                              onChange={(e) => setFinForm({ ...finForm, actuales: finForm.actuales.map((a) => a.instrumento === k ? { ...a, comision: e.target.value } : a) })}
                              className={inputCls}
                              data-testid={`circ-inst-com-${k}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Field>
              <div className="grid gap-5 sm:grid-cols-3">
                <Field label="Plazo medio de facturas (días)">
                  <input data-testid="circ-plazo" value={finForm.plazo_dias} onChange={(e) => setFinForm({ ...finForm, plazo_dias: e.target.value })} inputMode="numeric" className={inputCls} />
                </Field>
                <Field label="Descuento pronto pago (%)" hint="Opcional">
                  <input data-testid="circ-dpp-pct" value={finForm.dpp_pct} onChange={(e) => setFinForm({ ...finForm, dpp_pct: e.target.value })} inputMode="numeric" className={inputCls} />
                </Field>
                <Field label="Días de anticipo" hint="Opcional">
                  <input data-testid="circ-dpp-dias" value={finForm.dpp_dias} onChange={(e) => setFinForm({ ...finForm, dpp_dias: e.target.value })} inputMode="numeric" className={inputCls} />
                </Field>
              </div>
              <GoldButton loading={loading} testid="circ-step2-submit">Comparar instrumentos</GoldButton>
            </form>

            <div className="lg:sticky lg:top-10">
              {fin ? (
                <div className="space-y-6" data-testid="circ-fin-result">
                  <div className="border border-gold/20 bg-ink-3/60 p-7">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                      Perfil de mercado: {fin.perfil} · Índices a {fin.indices.actualizado} (Euríbor 12M {fmtPct(fin.indices.euribor_12m)} · 3M {fmtPct(fin.indices.euribor_3m)})
                    </p>
                    <div className="mt-5 space-y-4">
                      {fin.instrumentos.map((i) => (
                        <div key={i.key} className="border-b border-gold/10 pb-4" data-testid={`circ-row-${i.key}`}>
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-semibold text-white">{i.instrumento}</p>
                            <p className="font-serif text-lg text-gold-light">{fmtPct(i.cea_min)} – {fmtPct(i.cea_max)}</p>
                          </div>
                          <p className="mt-1 text-xs text-slate-400">
                            Para tu perfil: ~{fmtPct(i.cea_perfil)} → {fmt(i.coste_anual)} €/año sobre {fmt(fin.nof)} €
                          </p>
                          <p className="mt-1 text-xs text-slate-500">{i.ventajas} · {i.limitaciones}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {fin.actuales.length > 0 && (
                    <div className="border border-gold/30 bg-gold/[0.05] p-6" data-testid="circ-actual-comp">
                      {fin.actuales.map((a) => (
                        <p key={a.instrumento} className="text-sm text-slate-200">
                          Tu coste actual en {a.instrumento} es {fmtPct(a.cea_actual)} frente a un rango de mercado de {fmtPct(a.mercado_min)} – {fmtPct(a.mercado_max)} para tu perfil.
                        </p>
                      ))}
                    </div>
                  )}
                  {fin.pronto_pago && (
                    <div className="border-l-2 border-gold/50 bg-gold/[0.05] p-6" data-testid="circ-dpp-msg">
                      <p className="text-sm leading-relaxed text-slate-200">
                        El coste implícito de no aprovechar el descuento por pronto pago es de {fmtPct(fin.pronto_pago.cea)} anual. Si este coste es superior al de tu financiación bancaria, te conviene financiarte con el banco y aprovechar el descuento.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="circ-fin-empty">
                  <p className="text-sm leading-relaxed text-slate-400">
                    Completa el perfil de tu empresa y compara el coste efectivo anual de cada instrumento de financiación.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* LEAD */}
          <div className="mt-20 border-t border-gold/10 pt-16">
            <div className="mx-auto max-w-2xl">
              {done ? (
                <DonePanel
                  testid="circ-done"
                  title="Hemos enviado el plan a tu email."
                  subtitle="Un socio de AJG te contactará en 24-48 h para comentar los resultados de forma confidencial."
                  extra={
                    <button onClick={goServicios} data-testid="circ-servicios-link" className="mt-8 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all hover:bg-gold hover:text-ink">
                      ¿Quieres que controlemos tu circulante cada mes? Conoce nuestro servicio de seguimiento financiero mensual
                    </button>
                  }
                />
              ) : (
                <form onSubmit={submitLead} className="space-y-5" data-testid="circ-lead-form">
                  <h2 className="font-serif text-3xl text-white">Recibe tu plan de optimización del circulante</h2>
                  <p className="text-sm leading-relaxed text-slate-300">
                    Caja liberable con objetivos de días, combinación de instrumentos recomendada y ahorro anual estimado, en un informe PDF con tu diagnóstico completo.
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Nombre">
                      <input data-testid="circ-nombre" value={lead.nombre} onChange={(e) => setLead({ ...lead, nombre: e.target.value })} required className={inputCls} />
                    </Field>
                    <Field label="Empresa">
                      <input data-testid="circ-empresa" value={lead.empresa} onChange={(e) => setLead({ ...lead, empresa: e.target.value })} required className={inputCls} />
                    </Field>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Email">
                      <input data-testid="circ-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className={inputCls} />
                    </Field>
                    <Field label="Teléfono">
                      <input data-testid="circ-telefono" type="tel" value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} required className={inputCls} />
                    </Field>
                  </div>
                  <ConsentChecks lead={lead} setLead={setLead} prefix="circ" />
                  <GoldButton loading={leadLoading} testid="circ-lead-submit">Recibir mi plan de optimización del circulante</GoldButton>
                  <p className="text-xs leading-relaxed text-slate-500">{LEGAL} Índices actualizados a {fin?.indices?.actualizado || "última publicación disponible"}.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </ToolShell>
  );
}
